import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Eye, EyeOff, UserPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuthStore } from '@/stores/auth-store'

interface RegisterFormProps {
  onSuccess?: () => void
}

export function RegisterForm({ onSuccess }: RegisterFormProps) {
  const { register, loading, error, clearError, pendingConfirmationEmail, clearPendingConfirmation } = useAuthStore()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  })
  const [registrationSubmitted, setRegistrationSubmitted] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (error) clearError()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.email || !formData.password || !formData.name) {
      return
    }

    if (formData.password !== formData.confirmPassword) {
      return
    }

    if (formData.password.length < 6) {
      return
    }

    const success = await register(formData.email, formData.password, formData.name)
    if (!success) {
      return
    }

    const { pendingConfirmationEmail: nextPendingConfirmationEmail, isAuthenticated: nextIsAuthenticated } = useAuthStore.getState()
    if (nextPendingConfirmationEmail || !nextIsAuthenticated) {
      setRegistrationSubmitted(true)
      clearError()
      return
    }

    if (onSuccess) {
      onSuccess()
    }
  }

  const passwordMismatch = !!(formData.confirmPassword && formData.password !== formData.confirmPassword)
  const passwordTooShort = !!(formData.password && formData.password.length < 6)

  if (registrationSubmitted && pendingConfirmationEmail) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-gray-900">
            Bevestig je e-mailadres
          </CardTitle>
          <p className="mt-2 text-gray-600">
            We hebben een bevestigingsmail gestuurd naar <span className="font-medium text-gray-900">{pendingConfirmationEmail}</span>.
          </p>
        </CardHeader>

        <CardContent className="space-y-4 text-sm text-gray-600">
          <p>Klik op de link in die e-mail om je account te activeren. Pas daarna kun je de app volledig gebruiken.</p>
          <p>Geen e-mail ontvangen? Controleer ook je spam-map of probeer opnieuw te registreren.</p>

          <div className="flex flex-col gap-3 sm:flex-row">
            <Button asChild className="flex-1">
              <Link to="/login">Naar inloggen</Link>
            </Button>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => {
                setRegistrationSubmitted(false)
                clearPendingConfirmation()
              }}
            >
              Ander e-mailadres
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <CardTitle className="text-2xl font-bold text-gray-900">
          Registreren
        </CardTitle>
        <p className="text-gray-600 mt-2">
          Maak een account aan om te beginnen
        </p>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Naam
            </label>
            <Input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="Je naam"
              required
              autoComplete="name"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              E-mailadres
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="je@email.com"
              required
              autoComplete="email"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Wachtwoord
            </label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                required
                autoComplete="new-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {passwordTooShort && (
              <p className="text-red-600 text-xs mt-1">
                Wachtwoord moet minimaal 6 karakters zijn
              </p>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Bevestig wachtwoord
            </label>
            <div className="relative">
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
                required
                autoComplete="new-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {passwordMismatch && (
              <p className="text-red-600 text-xs mt-1">
                Wachtwoorden komen niet overeen
              </p>
            )}
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={
              loading || 
              !formData.email || 
              !formData.password || 
              !formData.name ||
              !formData.confirmPassword ||
              passwordMismatch ||
              passwordTooShort
            }
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Bezig met registreren...
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <UserPlus className="h-4 w-4 mr-2" />
                Account aanmaken
              </div>
            )}
          </Button>

          <div className="text-center text-sm text-gray-600">
            Al een account?{' '}
            <Link 
              to="/login" 
              className="text-orange-600 hover:text-orange-700 font-medium"
            >
              Log hier in
            </Link>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
